import React, { useRef } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import composeRequestArgs from './composeRequestArgs';
import { fetchData } from '../utils/fetchData';
import { fetchSuggestData } from '../utils/fetchSuggestData';
import formatPaginationData from '../components/atoms/Pagination/formatPaginationData';
import formatSortOrder from '../components/organisms/SearchResults/formatSortOrder';
import getPageOffset from '../components/atoms/Pagination/getPageOffset';
import composeInitialSort from './composeInitialSort';
import composeRuntimeParams from './composeRuntimeParams';
import resolveFacets from './resolveFacets';
import resolveSuggestions from './resolveSuggestions';
const SearchResultsContext = createContext({});
const SearchQuestionContext = createContext({});
export const showAllQuery = '!showall';
const tabNames = {
  saved: 'saved',
  all: 'all',
};
const DataProvider = ({ children, config, maxPages = 5, suggestConfig, localStorageKey, contentHeadings, moduleType = 'event' }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('query')?.trim() ?? showAllQuery;
  const initialTab = searchParams.get('tab')?.trim() == tabNames.saved ? tabNames.saved : tabNames.all;
  const [response, setResponse] = useState({});
  const [defaultResponse, setDefaultResponse] = useState({});
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [searchFilters, setSearchFilters] = useState({});
  const [suggestResponse, setSuggestResponse] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [resultTab, setResultTab] = useState(initialTab);
  const [isLoading, setIsLoading] = useState(true);
  const resultsRef = useRef(null);
  const results = response?.response?.resultPacket?.results || [];

  // resolve facets
  const facets = resolveFacets(response?.response?.facets, config.collection) || [];
  const allFacets = resolveFacets(defaultResponse?.response?.facets, config.collection) || [];

  // resolve any suggestions
  const suggestions = resolveSuggestions(suggestResponse, suggestConfig.collection);
  const resultsSummary = response?.response?.resultPacket?.resultsSummary || {};
  const pagination = formatPaginationData(resultsSummary, maxPages);
  let sortOrder = formatSortOrder(response?.question?.inputParameters?.sort ? response?.question?.inputParameters?.sort[0] : '');
  if(!sortOrder.field && sortOrder.direction == 'none' && config?.initialSort) {
      sortOrder = composeInitialSort(config.initialSort);
  }
  
  const getData = (args) => {
    setIsLoading(true);
    fetchData({ ...args, config }).then((data) => {
      setResponse(data);
      setIsLoading(false);
      if (args?.scroll) {
        resultsRef.current.scrollIntoView({ behavior: 'auto', block: 'center' });
      }
    });
  };
  // Set initial response on component mount
  useEffect(() => {
    if (!config?.endpoint || !config?.collection) return;
    let initialQuery = composeRequestArgs(searchParams);
    if (config?.initialSort) {
      initialQuery.sort = composeInitialSort(config.initialSort);
    }
    //configparams['meta_id'] = 'config'; // only return config (which contains all possible facet values)
    fetchData({ config }).then((data) => {
      setDefaultResponse(data);
    });

    initialQuery.params = { ...initialQuery.params, ...composeRuntimeParams({ moduleType: moduleType, filters: initialQuery?.params }) };
    getData(initialQuery);
  }, []);

  // Set query helper
  function setQuery(value) {
    const queryValue = value.trim() ? value.trim() : showAllQuery;
    setSearchQuery(queryValue);
    getData({ query: value, params: searchFilters, sort: sortOrder });
  }
  // Set filter helper
  function setSingleFilter(filter, value) {
    const filters = {
      ...searchFilters,
      [filter]: value,
    };
    setMultipleFilters(filters);
  }
  // Set multiple facet values
  function setMultipleFilters(filters) {
    setSearchFilters(filters);
    getData({ query: searchQuery, params: filters });
  }
  function setFormQuestion(formQuery, filters) {
    const queryValue = formQuery.trim() ? formQuery.trim() : showAllQuery;
    setResultTab(tabNames.all);
    setSearchQuery(queryValue);

    filters = { ...filters, ...composeRuntimeParams({ moduleType: moduleType, filters: filters }) };
    getData({ query: queryValue, params: filters, sort: sortOrder });
  }

  // Clear filters helper
  function clearFilters(clearSearchParams) {
    setSearchFilters({});
    //getData({ query: searchQuery })
    let initialQuery = composeRequestArgs(clearSearchParams);
    initialQuery.query = searchQuery;
    initialQuery.sort = sortOrder;
    initialQuery.params = { ...initialQuery.params, ...composeRuntimeParams({ moduleType: moduleType, filters: initialQuery?.params }) };
    getData(initialQuery);
    setResultTab(tabNames.all);
  }
  // Set pagination
  function setPage(pageNumber) {
    // calculate page from offset
    const limit = resultsSummary && resultsSummary.numRanks ? resultsSummary.numRanks : 10;
    const offset = getPageOffset(pageNumber, limit);
    getData(
      {
        query: searchQuery,
        params: searchFilters,
        offset,
        sort: sortOrder,
        scroll: true
      }
    );
  }
  function setSortOrder(field, direction) {
    getData({
      query: searchQuery,
      params: searchFilters,
      sort: { field, direction },
    });
  }
  function setSuggestQuery(searchQuery) {
    if (!searchQuery) setSuggestResponse({});
    else {
      let params = {
        config: suggestConfig,
        params: {},
        query: searchQuery,
      };
      fetchSuggestData(params).then(setSuggestResponse);
    }
  }
  // set the tab name on the query string and in state
  function setTab(name) {
    if (name === 'all') searchParams.delete('tab');
    else searchParams.set('tab', name);
    setSearchParams(searchParams);
    setResultTab(name);
  }
  const searchResultsContextValue = {
    config,
    facets,
    allFacets,
    pagination,
    query: response,
    results,
    resultsSummary,
    suggestions,
    showSuggestions,
    resultTab,
    localStorageKey,
    contentHeadings,
    resultsRef,
    moduleType,
    isLoading,
    tabNames,
  };
  const searchQuestionContextValue = {
    filters: searchFilters,
    query: searchQuery,
    sortOrder,
    clearFilters,
    setSingleFilter,
    setMultipleFilters,
    setFormQuestion,
    setPage,
    setQuery,
    setSortOrder,
    setSuggestQuery,
    setSuggestResponse,
    setShowSuggestions,
    setTab,
    setResponse,
  };
  return (
    <SearchResultsContext.Provider value={searchResultsContextValue}>
      <SearchQuestionContext.Provider value={searchQuestionContextValue}>{children}</SearchQuestionContext.Provider>
    </SearchResultsContext.Provider>
  );
};
export const useSearchResults = () => {
  return useContext(SearchResultsContext);
};
export const useSearchQuestion = () => {
  return useContext(SearchQuestionContext);
};
export default DataProvider;
